import React, { useState, useRef, useCallback } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Pencil, Eye, RefreshCw, Sparkles, ChevronDown, ChevronRight, AlertTriangle, Loader2, Monitor } from 'lucide-react';
import { ReportSection, REPORT_SECTIONS, ReportSectionId } from './types';

interface ReportSectionCardProps {
  section: ReportSection;
  onGenerate: (sectionId: ReportSectionId) => void;
  onSave: (sectionId: ReportSectionId, content: string) => void;
  onPinToScreen?: (sectionId: ReportSectionId, title: string, content: string) => void;
}

const mdComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-slate-900 mt-5 mb-3">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-slate-800 mt-4 mb-2 pb-1 border-b border-slate-100">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-slate-700 mt-3 mb-1.5">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold text-slate-700 mt-2 mb-1">{children}</h4>,
  p: ({ children }) => <p className="text-sm text-slate-700 leading-relaxed mb-2">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-slate-700 leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  em: ({ children }) => <em className="italic text-slate-600">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-3 border-primary/40 pl-3 my-2 text-sm italic text-slate-600 bg-slate-50 py-2 rounded-r">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isBlock = className?.includes('language-');
    return isBlock ? (
      <pre className="bg-slate-800 text-slate-100 rounded-lg p-3 my-2 overflow-x-auto text-xs">
        <code>{children}</code>
      </pre>
    ) : (
      <code className="bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
    );
  },
  table: ({ children }) => (
    <div className="overflow-x-auto my-3">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50 border-b border-slate-200">{children}</thead>,
  th: ({ children }) => <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 text-sm text-slate-700 border-b border-slate-100">{children}</td>,
  hr: () => <hr className="my-4 border-slate-200" />,
  a: ({ href, children }) => (
    <a href={href} className="text-primary underline hover:no-underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

export const ReportSectionCard: React.FC<ReportSectionCardProps> = ({ section, onGenerate, onSave, onPinToScreen }) => {
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(section.content || '');
  const [expanded, setExpanded] = useState(true);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const definition = REPORT_SECTIONS.find(d => d.id === section.id);
  const hasBeenEdited = !!section.editedAt;
  const hasContent = !!section.content;

  const handleEditToggle = () => {
    if (editing) {
      if (editContent !== section.content) {
        onSave(section.id, editContent);
      }
      setEditing(false);
    } else {
      setEditContent(section.content || '');
      setEditing(true);
    }
  };

  const handleContentChange = useCallback((value: string) => {
    setEditContent(value);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      onSave(section.id, value);
    }, 1500);
  }, [section.id, onSave]);

  const handleGenerate = () => {
    if (hasBeenEdited && hasContent) {
      setConfirmRegenerate(true);
    } else {
      onGenerate(section.id);
    }
  };

  const handleConfirmRegenerate = () => {
    setConfirmRegenerate(false);
    setEditing(false);
    onGenerate(section.id);
  };

  const readinessScore = section.readiness?.score;
  const readinessBadge = readinessScore != null ? (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
      readinessScore >= 70 ? 'bg-emerald-100 text-emerald-700' :
      readinessScore >= 40 ? 'bg-amber-100 text-amber-700' :
      'bg-red-100 text-red-700'
    }`}>
      {readinessScore}% ready
    </span>
  ) : null;

  return (
    <div
      className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-colors ${
        section.isGenerating ? 'border-primary/40 ring-1 ring-primary/20' : 'border-slate-200'
      }`}
      id={`report-section-${section.id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
        <button onClick={() => setExpanded(!expanded)} className="text-slate-400 hover:text-slate-600">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900">{definition?.title || section.id}</h3>
            {readinessBadge}
            {section.isGenerating && (
              <span className="flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary animate-pulse">
                <Loader2 size={10} className="animate-spin" />
                Generating...
              </span>
            )}
            {!section.isGenerating && hasContent && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                Generated
              </span>
            )}
            {hasBeenEdited && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                Edited
              </span>
            )}
          </div>
          {!expanded && definition && (
            <p className="text-xs text-slate-500 mt-0.5 truncate">{definition.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {hasContent && !section.isGenerating && onPinToScreen && (
            <button
              onClick={() => onPinToScreen(section.id, definition?.title || section.id, section.content!)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/10 transition-colors"
              title="Push to second screen"
            >
              <Monitor size={16} />
            </button>
          )}
          {hasContent && !section.isGenerating && (
            <button
              onClick={handleEditToggle}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              title={editing ? 'Preview' : 'Edit'}
            >
              {editing ? <Eye size={16} /> : <Pencil size={16} />}
            </button>
          )}
          <button
            onClick={handleGenerate}
            disabled={section.isGenerating}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              section.isGenerating
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : hasContent
                ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                : 'bg-primary text-white hover:bg-primary-dark'
            }`}
          >
            {section.isGenerating ? (
              <>
                <RefreshCw size={14} className="animate-spin" />
                Generating...
              </>
            ) : hasContent ? (
              <>
                <RefreshCw size={14} />
                Regenerate
              </>
            ) : (
              <>
                <Sparkles size={14} />
                Generate
              </>
            )}
          </button>
        </div>
      </div>

      {/* Body */}
      {expanded && (
        <div className="px-4 py-3">
          {/* Generating indicator */}
          {section.isGenerating && (
            <div className="mb-3 p-4 bg-gradient-to-r from-primary/5 to-blue-50 rounded-lg border border-primary/10">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Loader2 size={20} className="animate-spin text-primary" />
                  <div className="absolute inset-0 animate-ping opacity-20">
                    <Loader2 size={20} className="text-primary" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">AI is generating this section...</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Querying knowledge graph and composing content. This usually takes 15-30 seconds.
                  </p>
                </div>
              </div>
              {/* Animated progress bar */}
              <div className="mt-3 h-1 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full animate-[indeterminate_1.5s_ease-in-out_infinite]"
                  style={{ width: '40%', animation: 'indeterminate 1.5s ease-in-out infinite' }}
                />
              </div>
            </div>
          )}

          {/* Missing data hints */}
          {section.readiness?.missingData && section.readiness.missingData.length > 0 && !hasContent && !section.isGenerating && (
            <div className="mb-3 p-2.5 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-xs font-medium text-amber-700 mb-1">Missing data for this section:</p>
              <ul className="list-disc list-inside text-xs text-amber-600 space-y-0.5">
                {section.readiness.missingData.map((hint, i) => (
                  <li key={`${hint}-${i}`}>{hint}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Content */}
          {hasContent && !section.isGenerating ? (
            editing ? (
              <textarea
                value={editContent}
                onChange={(e) => handleContentChange(e.target.value)}
                className="w-full min-h-[200px] p-3 text-sm font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                placeholder="Edit section content (markdown supported)..."
              />
            ) : (
              <div className="max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{section.content!}</ReactMarkdown>
              </div>
            )
          ) : !section.isGenerating ? (
            <div className="text-center py-6">
              <p className="text-sm text-slate-400">
                {definition?.description || 'Click Generate to create this section from your session data.'}
              </p>
            </div>
          ) : null}

          {/* Timestamps */}
          {hasContent && !section.isGenerating && (
            <div className="flex gap-3 mt-3 pt-2 border-t border-slate-100">
              {section.generatedAt && (
                <span className="text-[10px] text-slate-400">
                  Generated {new Date(section.generatedAt).toLocaleString()}
                </span>
              )}
              {section.editedAt && (
                <span className="text-[10px] text-blue-400">
                  Last edited {new Date(section.editedAt).toLocaleString()}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Confirm regenerate dialog */}
      {confirmRegenerate && (
        <div className="px-4 py-3 bg-amber-50 border-t border-amber-100 flex items-center gap-3">
          <AlertTriangle size={16} className="text-amber-500 shrink-0" />
          <p className="text-xs text-amber-700 flex-1">
            This will overwrite your edits. Continue?
          </p>
          <button
            onClick={() => setConfirmRegenerate(false)}
            className="px-2.5 py-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmRegenerate}
            className="px-2.5 py-1 text-xs font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600"
          >
            Regenerate
          </button>
        </div>
      )}
    </div>
  );
};
